// components/contratBDC.ts
// Génération du contrat de bon de caisse (PDF) — partagée par la création
// (le créateur imprime, fait signer et charge le contrat) et la validation.
// Présentation inspirée du modèle FastReport « contrat d'investissement » :
// en-tête banque, bandeaux de sections, tableaux libellé/valeur, clauses,
// zone de signature et pied de page.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import formatNumber from "../../../Functions/Traitement/FormatNumber";
import { Crypter } from "../../../Functions/Security/Crypter";
import Data from '../../../Data/Data.json';

const fmtDate = (d: any) => {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString('fr-FR');
};

const DARK_GREEN: [number, number, number] = [0, 100, 0];
const HONEYDEW: [number, number, number] = [240, 255, 240];
const WHITESMOKE: [number, number, number] = [245, 245, 245];
const DARK_RED: [number, number, number] = [139, 0, 0];
const MISTYROSE: [number, number, number] = [255, 228, 225];
const LIGHTGRAY: [number, number, number] = [211, 211, 211];

export interface ContratInfos {
  banque?: string;
  agence?: string;
  codage?: string;
  nomuti?: string;
  datjour?: any;
  ville?: string;
  penalite?: string;   // montant formaté de la pénalité d'interruption (grille)
}

// Pénalité contractuelle d'interruption : montant retourné par la grille
// tarifaire du code opération CODOPEPENAL du type de bon, sur la base
// MONTANT × jours restants / 360 — au moment du contrat, les jours
// restants couvrent toute la durée (base = MONTANT × DUREE / 360).
export async function fetchPenaliteInterruption(typBdc: any, codage: string, montant: number, duree: number, authHeader: any): Promise<string> {
  if (!typBdc || !montant || !duree) return '';
  try {
    const base = Math.round(montant * duree / 360);
    const response = await axios({
      method: 'get',
      url: Data.ApiUrl.ApercuFraisInterruptionBDC,
      headers: {
        'CypherTypBdc': Crypter.encrypt(JSON.stringify(typBdc)),
        'CypherAgence': Crypter.encrypt(JSON.stringify(codage)),
        'CypherMontant': Crypter.encrypt(JSON.stringify(String(base))),
        'Authorization': authHeader(),
        'Content-Type': 'application/json'
      }
    });
    const res = response.data ? JSON.parse(Crypter.decrypt(response.data)) : null;
    const frais = res?.Result?.Table1?.[0]?.Frais;
    return Number(frais) > 0 ? formatNumber(Number(frais)) : '';
  } catch {
    return '';
  }
}

// v : lecteur de champ (form + bon sélectionné)
// infos : banque / agence / utilisateur / jour comptable (session)
export function genererContratBDC(v: (f: string) => any, codage: string, infos: ContratInfos = {}) {
  // jsPDF ne rend pas les espaces insécables (U+00A0, U+202F, U+2009) :
  // formatNumber les afficherait comme '/'. On normalise en espace simple.
  const fmtAmt = (x: any) => String(x ?? "").replace(/[    ]/g, " ");
  const banque = infos.banque || 'AnyBank';
  const agence = infos.agence || codage || '';
  const ville = infos.ville || agence;
  const nomuti = infos.nomuti || '';
  const datjour = fmtDate(infos.datjour) || new Date().toLocaleDateString('fr-FR');
  const numero = v('NUMBDC') || 'EN PRÉPARATION';

  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 40;
  const CW = W - 2 * M;
  let y = 50;

  const pageBreak = (need: number) => {
    if (y + need > H - 60) { doc.addPage(); y = 50; }
  };

  // Bandeau de section : rectangle vert plein + texte blanc gras
  const section = (titre: string) => {
    pageBreak(34);
    doc.setFillColor(...DARK_GREEN);
    doc.rect(M, y, CW, 20, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.text('  ' + titre, M + 2, y + 13.5);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    y += 26;
  };

  // Tableau libellé / valeur bordé (style du modèle)
  const tableLV = (rows: [string, string][], boldVals: number[] = [], colorVals: Record<number, [number, number, number]> = {}) => {
    autoTable(doc, {
      startY: y, margin: { left: M, right: M },
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 4, textColor: 0, lineColor: LIGHTGRAY, lineWidth: 0.6 },
      columnStyles: { 0: { cellWidth: 185, fillColor: WHITESMOKE, fontStyle: 'bold' }, 1: { cellWidth: CW - 185 } },
      body: rows,
      didParseCell: (d: any) => {
        if (d.section === 'body' && d.column.index === 1) {
          if (boldVals.includes(d.row.index)) d.cell.styles.fontStyle = 'bold';
          if (colorVals[d.row.index]) d.cell.styles.textColor = colorVals[d.row.index];
        }
      },
    });
    y = (doc as any).lastAutoTable.finalY + 10;
  };

  // ----------------------------------------------------------------
  // EN-TÊTE BANQUE
  // ----------------------------------------------------------------
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...DARK_GREEN);
  doc.text(banque, M, y);
  doc.setFontSize(9); doc.setTextColor(0, 0, 0);
  y += 15;
  doc.text(agence + (codage ? ' — agence ' + codage : ''), M, y); y += 13;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
  doc.text("Bon de caisse — produit d'épargne à terme", M, y);

  // Statut + traçabilité, à droite — le cartouche porte toujours le
  // libellé « CONTRAT » ; sans numéro de bon, une mention de projet
  // est ajoutée en dessous (document non encore définitif).
  const signe = !!v('NUMBDC');
  doc.setDrawColor(...DARK_GREEN);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5);
  doc.rect(W - M - 190, 42, 190, 20);
  doc.setTextColor(...DARK_GREEN);
  doc.text('CONTRAT', W - M - 190 + 95, 55, { align: 'center' });
  doc.setTextColor(...(signe ? DARK_GREEN : DARK_RED));
  doc.setFontSize(7); doc.setFont('helvetica', 'italic');
  
  doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'italic'); doc.setFontSize(7.5);
  doc.text('Imprimé le ' + datjour + (nomuti ? ' par ' + nomuti : ''), W - M, 78, { align: 'right' });

  // Ligne verte + titre + sous-titre
  y = 95;
  doc.setFillColor(...DARK_GREEN);
  doc.rect(M, y, CW, 2, 'F');
  y += 16;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(15);
  doc.text("CONTRAT DE BON DE CAISSE — " + (v('lib_TYPBDC') || v('TYPBDC') || ''), W / 2, y, { align: 'center' });
  y += 15;
  doc.setFont('helvetica', 'italic'); doc.setFontSize(9.5);
  doc.text("Souscription d'un bon de caisse à terme", W / 2, y, { align: 'center' });
  y += 12;

  // Cartouche vert avec le numéro du document
  const boxW = 220;
  doc.setFillColor(...DARK_GREEN);
  doc.rect((W - boxW) / 2, y, boxW, 24, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
  doc.text('N° ' + numero, W / 2, y + 16, { align: 'center' });
  doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'normal');
  y += 40;

  // ----------------------------------------------------------------
  // I. PARTIES AU CONTRAT
  // ----------------------------------------------------------------
  section('I. PARTIES AU CONTRAT');
  autoTable(doc, {
    startY: y, margin: { left: M, right: M },
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 5, textColor: 0, lineColor: DARK_GREEN, lineWidth: 0.8 },
    columnStyles: { 0: { cellWidth: 185, fillColor: HONEYDEW, fontStyle: 'bold' }, 1: { cellWidth: CW - 185 } },
    body: [
      ['LE SOUSCRIPTEUR (client)', (v('NUMCLI') || '') + ' — ' + (v('lib_NUMCLI') || v('NOMCLI') || '')],
      ['LA BANQUE', banque + ' — ' + agence],
    ],
  });
  y = (doc as any).lastAutoTable.finalY + 10;

  // ----------------------------------------------------------------
  // II. CARACTÉRISTIQUES DU BON DE CAISSE
  // ----------------------------------------------------------------
  section("II. CARACTÉRISTIQUES DU BON DE CAISSE");
  tableLV([
    ['Type de bon de caisse', (v('lib_TYPBDC') || '') + (v('TYPBDC') ? ' (' + v('TYPBDC') + ')' : '')],
    ['Montant déposé', fmtAmt(formatNumber(Number(v('MONTANT')) || 0))],
    ['Durée', String(v('DUREE') ?? '') + (v('lib_FREQPROV') ? ' — périodicité : ' + v('lib_FREQPROV') : '')],
    ['Date de valeur / Date d\'échéance', fmtDate(v('DATDEBUT')) + '   /   ' + fmtDate(v('DATFIN'))],
    ['Compte débité', v('CPTDEPOT') || ''],
    ['Compte du bon de caisse', v('CPTBDC') || ''],
    ['Compte intérêts du client', v('CPTINTCLI') || ''],
    ['Référence du bon', v('REFBDC') || ''],
    ['Simulation n°', v('CODSIMUL') || '—'],
  ], [1]);

  // ----------------------------------------------------------------
  // III. RÉMUNÉRATION ET MODALITÉS
  // ----------------------------------------------------------------
  section('III. RÉMUNÉRATION ET MODALITÉS');
  const imput = (v('TYPIMPUTINT') || '').toUpperCase();
  tableLV([
    ['Mode d\'imputation des intérêts', imput === 'PRE' ? 'Précompté — intérêts versés à la mise en place' : imput === 'POST' ? 'Postcompté — intérêts versés à l\'échéance' : (v('TYPIMPUTINT') || '')],
    ['Taux d\'intérêt annuel', String(v('TXINT') ?? '') + ' %'],
    ['Taux de la retenue fiscale', String(v('TXTAXE') ?? '') + ' %'],
    ['Intérêts bruts', fmtAmt(v('TotInt') || formatNumber(Number(v('MTNINT')) || 0))],
    ['Retenue fiscale', fmtAmt(v('TotTaxe') || formatNumber(Number(v('MTNTAXE')) || 0))],
    ['Net à payer au client', fmtAmt(v('TotNet') || formatNumber(Number(v('MTNNETAPAYER')) || 0))],
    ['Frais de souscription', fmtAmt(v('FRAISSOUSCR') || '—')],
    ['Interruption anticipée', infos.penalite
      ? `En cas d'interruption anticipée, une pénalité de ${fmtAmt(infos.penalite)} sera prélevée (montant du bon x jours restants / 360, selon la grille tarifaire du code opération paramétré pour ce type de bon)`
      : "Selon le paramétrage du type de bon de caisse (durée minimale et grille de pénalité applicables)"],
  ], [5], { 5: DARK_GREEN });

  // Avertissement (cadre rouge, fond rosé)
  pageBreak(44);
  doc.setDrawColor(...DARK_RED);
  doc.setFillColor(...MISTYROSE);
  const warn = "AVERTISSEMENT : toute demande d'interruption anticipée est soumise à validation et aux conditions définies dans le paramétrage du type de bon de caisse ; les pénalités et taxes alors applicables sont retenues sur les sommes versées.";
  const warnLines = doc.splitTextToSize(warn, CW - 16);
  const warnH = warnLines.length * 11 + 10;
  doc.rect(M, y, CW, warnH, 'FD');
  doc.setFont('helvetica', 'bolditalic'); doc.setFontSize(8.5); doc.setTextColor(...DARK_RED);
  doc.text(warnLines, M + 8, y + 13);
  doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'normal');
  y += warnH + 10;

  // ----------------------------------------------------------------
  // IV. STIPULATIONS
  // ----------------------------------------------------------------
  section('IV. STIPULATIONS');
  const clauses: [string, string][] = [
    ['1. Objet', "Par le présent contrat, le souscripteur remet à la banque les fonds indiqués ci-dessus, qui sont bloqués sous la forme d'un bon de caisse pour la durée convenue. Le présent bon est nominatif et non négociable ; il ne peut être cédé ni donné en garantie sans l'accord préalable de la banque."],
    ['2. Rémunération', "Le bon de caisse porte intérêt au taux annuel indiqué, calculé sur la base d'une année de 360 jours. Les intérêts sont versés selon le mode d'imputation du type de bon : précompté (versés à la mise en place) ou postcompté (versés à l'échéance, après dotations périodiques)."],
    ['3. Fiscalité', "Les intérêts sont soumis à la retenue fiscale à la source au taux en vigueur à la date du contrat. La retenue est opérée par la banque et reversée à l'administration fiscale ; le client perçoit le net à payer indiqué ci-dessus."],
    ['4. Échéance', "À la date d'échéance, la banque débloque le principal sur le compte du client et verse les intérêts nets de retenue sur le compte intérêts désigné, sauf renouvellement ou instruction contraire notifiée avant l'échéance."],
    ['5. Interruption anticipée', "Toute interruption avant l'échéance n'est possible que si le type de bon de caisse le permet et sous réserve du respect de la durée minimale paramétrée. Elle est soumise à une demande validée selon le principe des quatre yeux. La pénalité d'interruption est alors calculée sur la base du montant du bon multiplié par le nombre de jours restants divisé par 360, selon la grille tarifaire du code opération paramétré" + (infos.penalite ? " — soit " + fmtAmt(infos.penalite) + " pour ce contrat" : "") + ". La pénalité est prélevée sur le compte du client au profit des comptes rattachés à ce code opération."],
    ['6. Pièces contractuelles', "Font partie intégrante du contrat : la présente convention signée par le client, les conditions particulières du type de bon de caisse en vigueur à la date de souscription et, le cas échéant, la simulation ayant précédé la souscription."],
  ];
  doc.setFontSize(8.5);
  clauses.forEach(([titre, texte]) => {
    const lines = doc.splitTextToSize(titre + ' — ' + texte, CW);
    pageBreak(lines.length * 11 + 8);
    doc.text(lines, M, y + 8);
    y += lines.length * 11 + 8;
  });
  y += 8;

  // ----------------------------------------------------------------
  // SIGNATURES
  // ----------------------------------------------------------------
  pageBreak(130);
  doc.setFont('helvetica', 'italic'); doc.setFontSize(9);
  doc.text('Fait à ' + (ville || '________') + ', le ' + datjour, W - M, y, { align: 'right' });
  y += 12;
  autoTable(doc, {
    startY: y, margin: { left: M, right: M },
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 5, lineColor: DARK_GREEN, lineWidth: 0.8 },
    head: [["LE SOUSCRIPTEUR\n(lu et approuvé)", "POUR LA BANQUE\n(signature et cachet)"]],
    headStyles: { fillColor: HONEYDEW, textColor: DARK_GREEN, fontStyle: 'bold', halign: 'center' },
    body: [['\n\n\n\n', '\n\n\n\n']],
    columnStyles: { 0: { cellWidth: CW / 2 }, 1: { cellWidth: CW / 2 } },
  });
  y = (doc as any).lastAutoTable.finalY;

  // ----------------------------------------------------------------
  // PIED DE PAGE (toutes les pages)
  // ----------------------------------------------------------------
  const pages = (doc as any).getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFillColor(...WHITESMOKE);
    doc.rect(0, H - 30, W, 30, 'F');
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(0, 0, 0);
    doc.text('Page ' + i + '/' + pages, M, H - 12);
    doc.setFont('helvetica', 'italic'); doc.setFontSize(7);
    doc.text('Document contractuel — modèle soumis à validation du service juridique.', W - M, H - 12, { align: 'right' });
  }

  doc.save("Contrat_BDC_" + (v('NUMBDC') || 'projet') + ".pdf");
}
